/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import './index.scss';
import { Form, Select, Button } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';
import SimpleModal from 'components/common/modals/SimpleModal';
import PINInput from 'components/common/PINInput';
import telcoOptions from 'constants/options/telcoOptions';
import { toDropDownOptions } from 'utils/dropdownOptions';
import approveLoanRequests from 'redux/actions/loanRequests/approveLoanRequests';
import getCurrentOrg from 'helpers/getCurrentOrg';
import isFieldRequired from 'helpers/validators/isFieldRequired';
import allFieldsRequired from 'helpers/validators/validateFields';
import currencyFormat from 'utils/currencyFormat';

const LoanApprovalModal = ({
  open,
  setOpen,
  request,
  onSuccess,
  wallets,
  role,
}) => {
  const { push } = useHistory();
  const { loading } = useSelector(
    ({ loanRequests: { approveLoanRequest } }) => approveLoanRequest,
  );

  const currentUserOrganization = getCurrentOrg();
  const [form, setForm] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const dispatch = useDispatch();

  const selectedChannelWallet = wallets?.find(
    wallet => wallet.provider_name === form.channel,
  );

  const handleSubmit = () => {
    const isRequired = allFieldsRequired(
      role === 'loan_admin'
        ? ['channel', 'pinNumber']
        : ['pinNumber'],
      form,
    );

    if (isRequired) {
      setShowErrors(false);

      // Check if selected channel has sufficient balance
      if (role === 'loan_admin' && selectedChannelWallet) {
        if (selectedChannelWallet.micro_loan_fund < request?.amount) {
          return setShowErrors(true);
        }
      }

      return approveLoanRequests(
        currentUserOrganization.id,
        request?.id,
        {
          channel: form.channel || undefined,
          password: form.pinNumber,
          admin: role,
        },
      )(dispatch, () => {
        onSuccess?.();
        setOpen(null);
        if (currentUserOrganization?.id) {
          push(
            `/organizations/${currentUserOrganization.id}/requests/${request?.id}`,
          );
        }
      });
    }
    return setShowErrors(true);
  };

  const handleFormChange = (e, { name, value }) => {
    setForm({ ...form, [name]: value });
  };

  return (
    <SimpleModal
      open={open}
      title="Confirm Loan Approval"
      onClose={() => setOpen(null)}
    >
      <div className="padding confirm-loan-approval">
        <Form onSubmit={handleSubmit} loading={loading}>
          {role === 'loan_admin' && (
            <Form.Dropdown
              name="channel"
              control={Select}
              fluid
              options={toDropDownOptions(telcoOptions, true)}
              className="confirm-loan-approval__select-input"
              label="Select channel"
              placeholder="Select"
              onChange={handleFormChange}
              error={isFieldRequired(
                showErrors,
                !form?.channel,
                'Channel',
              )}
            />
          )}
          {selectedChannelWallet && (
            <>
              <div className=" flex flex-between confirm-loan-amend__channel-balance">
                <span className="confirm-loan-amend__channel-balance__label">
                  Micro loan fund
                </span>
                <span className="confirm-loan-amend__channel-balance__value">
                  {currencyFormat(
                    selectedChannelWallet?.micro_loan_fund,
                  )}
                </span>
              </div>

              {selectedChannelWallet.micro_loan_fund <
                form.amount && (
                <div
                  className=" flex flex-between confirm-loan-amend__channel-balance"
                  style={{
                    color: '#9f3a38',
                    marginBottom: '0.5rem',
                  }}
                >
                  Insufficient balance. Available:{' '}
                  {currencyFormat(
                    selectedChannelWallet.micro_loan_fund,
                  )}
                  , Required: {currencyFormat(form.amount)}
                </div>
              )}
            </>
          )}

          <span>PIN</span>
          <PINInput
            fluid
            size="tiny"
            label="PIN"
            name="pinNumber"
            className="confirm-loan-approval__pin-input"
            onInputChange={handleFormChange}
            error={{
              message: isFieldRequired(
                showErrors,
                !form?.pinNumber,
                'PIN Number',
              ),
            }}
          />

          <Button
            disabled={
              loading ||
              (role === 'loan_admin' &&
                selectedChannelWallet &&
                selectedChannelWallet.micro_loan_fund <
                  request?.amount)
            }
            loading={loading}
            primary
            fluid
            size="medium"
            type="submit"
            content="Confirm"
          />
        </Form>
      </div>
    </SimpleModal>
  );
};

LoanApprovalModal.defaultProps = {
  onSuccess: () => null,
  wallets: [],
};

LoanApprovalModal.propTypes = {
  setOpen: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  open: PropTypes.bool.isRequired,
  request: PropTypes.object.isRequired,
  role: PropTypes.string.isRequired,
  wallets: PropTypes.arrayOf(PropTypes.object),
};

export default LoanApprovalModal;
